import { useEffect, useState } from "react";
import { useLanguage } from "@/hooks/useLanguage";
import { getSlogan, getWcCampaign } from "@/lib/branding/slogan";

interface Props {
  /** Total visible time (ms). Defaults to 4200. */
  durationMs?: number;
  /** localStorage key to gate repeat displays. Null disables gating. */
  sessionKey?: string | null;
  /** Force-show every mount (useful for showcase autoplay). */
  force?: boolean;
}

/**
 * Cinematic VerseSport intro overlay — premium fade.
 *   VerseSport
 *   Inteligencia deportiva en tiempo real.
 *   ─────
 *   Donde el Mundial cobra vida.
 *
 * Mobile-safe, GPU-light (only opacity transitions), auto-dismisses.
 */
export function CinematicBrandIntro({
  durationMs = 4200,
  sessionKey = "vs:cinematic_intro_seen",
  force = false,
}: Props) {
  const { lang } = useLanguage();
  const [phase, setPhase] = useState<"hidden" | "core" | "campaign" | "fadeout" | "gone">("hidden");

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!force && sessionKey) {
      try {
        if (window.sessionStorage.getItem(sessionKey) === "1") {
          setPhase("gone");
          return;
        }
        window.sessionStorage.setItem(sessionKey, "1");
      } catch { /* noop */ }
    }
    const t1 = window.setTimeout(() => setPhase("core"), 80);
    const t2 = window.setTimeout(() => setPhase("campaign"), Math.floor(durationMs * 0.45));
    const t3 = window.setTimeout(() => setPhase("fadeout"), durationMs - 600);
    const t4 = window.setTimeout(() => setPhase("gone"), durationMs);
    return () => { [t1, t2, t3, t4].forEach(window.clearTimeout); };
  }, [durationMs, force, sessionKey]);

  if (phase === "gone" || phase === "hidden") return null;

  const opacity = phase === "fadeout" ? 0 : 1;

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 z-[60] flex items-center justify-center bg-background/85 backdrop-blur-md transition-opacity duration-500"
      style={{ opacity }}
    >
      <div className="px-6 text-center">
        <div className="text-3xl font-black tracking-tight sm:text-5xl">
          <span className="bg-gradient-to-r from-primary to-foreground bg-clip-text text-transparent">
            VerseSport
          </span>
        </div>
        <p
          className="mt-3 text-[11px] font-medium uppercase tracking-[0.32em] text-muted-foreground transition-opacity duration-700 sm:text-xs"
          style={{ opacity: phase === "core" || phase === "campaign" ? 1 : 0 }}
        >
          {getSlogan(lang)}
        </p>
        <div className="mx-auto mt-5 h-px w-16 bg-border/60" />
        <p
          className="mt-5 text-base font-bold italic text-foreground/90 transition-opacity duration-700 sm:text-lg"
          style={{ opacity: phase === "campaign" ? 1 : 0 }}
        >
          {getWcCampaign(lang)}
        </p>
      </div>
    </div>
  );
}